import { ActorAgentSchema } from './AuditContextSchemas.js';
/**
 * Canonical HTTP header names and utilities for passing audit context.
 *
 * Audit context (correlationId, requestId, actorType, userId, etc.) is
 * passed between services using these standard headers. This module provides
 * the header name constants and functions to convert between audit context
 * objects and header key-value pairs.
 */
/**
 * Standard HTTP header names for audit context.
 * Used by all services and clients for consistent audit trail propagation.
 */
export const AUDIT_HEADERS = {
    CORRELATION_ID: 'x-correlation-id',
    REQUEST_ID: 'x-request-id',
    ACTOR_TYPE: 'x-actor-type',
    USER_ID: 'x-user-id',
    CONTACT_ID: 'x-contact-id',
    SESSION_ID: 'x-session-id',
    IP_ADDRESS_HASH: 'x-ip-address-hash',
    IS_ADMIN: 'x-is-admin',
    BULK_IMPORT: 'x-bulk-import',
    ORGANIZATION_ID: 'x-organization-id',
    ACTOR_AGENT: 'x-actor-agent',
};
const VALID_ACTOR_TYPES = new Set(['user', 'system', 'contact']);
const PLUS = /\+/g;
const SLASH = /\//g;
const DASH = /-/g;
const UNDERSCORE = /_/g;
const TRAILING_PADDING = /=+$/;
/**
 * Encodes the agent as base64url JSON. The encoding keeps non-ASCII names
 * safe in an HTTP header. It uses no Node APIs, so frontend code can import it.
 */
export function encodeActorAgentHeader(actorAgent) {
    const bytes = new TextEncoder().encode(JSON.stringify(actorAgent));
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(PLUS, '-').replace(SLASH, '_').replace(TRAILING_PADDING, '');
}
/**
 * Decodes an agent header value. Returns undefined on any failure, invalid
 * UTF-8 included, because a bad agent header must never reject the request.
 */
export function decodeActorAgentHeader(value) {
    try {
        const padded = value.replace(DASH, '+').replace(UNDERSCORE, '/');
        const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const parsed = ActorAgentSchema.safeParse(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)));
        return parsed.success ? parsed.data : undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * Converts an audit context object into HTTP header key-value pairs.
 * Accepts the shape used by body.context or query params (field names like
 * correlationId, requestId, actorType, userId, etc.) and maps them to
 * canonical header names.
 *
 * Only includes fields that are present and non-undefined.
 *
 * @param context - Audit context object (body.context shape or query params shape)
 * @returns Record of header name → string value pairs
 */
export function auditContextToHeaders(context) {
    const headers = {};
    if (context.correlationId) {
        headers[AUDIT_HEADERS.CORRELATION_ID] = context.correlationId;
    }
    if (context.requestId) {
        headers[AUDIT_HEADERS.REQUEST_ID] = context.requestId;
    }
    if (context.actorType && VALID_ACTOR_TYPES.has(context.actorType)) {
        headers[AUDIT_HEADERS.ACTOR_TYPE] = context.actorType;
    }
    if (context.userId) {
        headers[AUDIT_HEADERS.USER_ID] = context.userId;
    }
    if (context.contactId) {
        headers[AUDIT_HEADERS.CONTACT_ID] = context.contactId;
    }
    if (context.sessionId) {
        headers[AUDIT_HEADERS.SESSION_ID] = context.sessionId;
    }
    if (context.ipAddressHash) {
        headers[AUDIT_HEADERS.IP_ADDRESS_HASH] = context.ipAddressHash;
    }
    if (context.isAdmin !== undefined) {
        headers[AUDIT_HEADERS.IS_ADMIN] = String(context.isAdmin);
    }
    if (context.bulkImport !== undefined) {
        headers[AUDIT_HEADERS.BULK_IMPORT] = String(context.bulkImport);
    }
    if (context.organizationId) {
        headers[AUDIT_HEADERS.ORGANIZATION_ID] = context.organizationId;
    }
    if (context.actorAgent) {
        headers[AUDIT_HEADERS.ACTOR_AGENT] = encodeActorAgentHeader(context.actorAgent);
    }
    return headers;
}
/**
 * Resolves a header value that may be a string or string array.
 * HTTP headers can have multiple values; we take the first one.
 */
function resolveHeaderValue(value) {
    if (Array.isArray(value)) {
        return value.at(0);
    }
    return value;
}
/**
 * Parses HTTP headers into a typed audit context object.
 * Returns undefined if the minimum required fields (correlationId and requestId)
 * are not present.
 *
 * @param headers - HTTP headers (Fastify's IncomingHttpHeaders shape or similar)
 * @returns Parsed audit context values, or undefined if insufficient headers
 */
export function parseAuditContextHeaders(headers) {
    const correlationId = resolveHeaderValue(headers[AUDIT_HEADERS.CORRELATION_ID]);
    const requestId = resolveHeaderValue(headers[AUDIT_HEADERS.REQUEST_ID]);
    if (!(correlationId && requestId)) {
        return undefined;
    }
    const actorType = resolveHeaderValue(headers[AUDIT_HEADERS.ACTOR_TYPE]);
    const userId = resolveHeaderValue(headers[AUDIT_HEADERS.USER_ID]);
    const contactId = resolveHeaderValue(headers[AUDIT_HEADERS.CONTACT_ID]);
    const sessionId = resolveHeaderValue(headers[AUDIT_HEADERS.SESSION_ID]);
    const ipAddressHash = resolveHeaderValue(headers[AUDIT_HEADERS.IP_ADDRESS_HASH]);
    const isAdminRaw = resolveHeaderValue(headers[AUDIT_HEADERS.IS_ADMIN]);
    const bulkImportRaw = resolveHeaderValue(headers[AUDIT_HEADERS.BULK_IMPORT]);
    const organizationId = resolveHeaderValue(headers[AUDIT_HEADERS.ORGANIZATION_ID]);
    const actorAgentRaw = resolveHeaderValue(headers[AUDIT_HEADERS.ACTOR_AGENT]);
    const result = {
        correlationId,
        requestId,
    };
    if (actorType && VALID_ACTOR_TYPES.has(actorType)) {
        result.actorType = actorType;
    }
    if (userId) {
        result.userId = userId;
    }
    if (contactId) {
        result.contactId = contactId;
    }
    if (sessionId) {
        result.sessionId = sessionId;
    }
    if (ipAddressHash) {
        result.ipAddressHash = ipAddressHash;
    }
    if (isAdminRaw === 'true' || isAdminRaw === 'false') {
        result.isAdmin = isAdminRaw === 'true';
    }
    if (bulkImportRaw === 'true' || bulkImportRaw === 'false') {
        result.bulkImport = bulkImportRaw === 'true';
    }
    if (organizationId) {
        result.organizationId = organizationId;
    }
    const actorAgent = actorAgentRaw ? decodeActorAgentHeader(actorAgentRaw) : undefined;
    if (actorAgent) {
        result.actorAgent = actorAgent;
    }
    return result;
}
//# sourceMappingURL=AuditContextHeaders.js.map